// ==============================================
// My Bookings Page
// ==============================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { bookingsAPI } from '../services/api';
import { Booking, BookingParticipant, User } from '../types';
import { clubPath } from '../utils/slug';
import Modal from '../components/Modal';
import PlayerSearchPicker from '../components/PlayerSearchPicker';
import { useAuth } from '../contexts/AuthContext';

const SITE_URL = process.env.REACT_APP_SITE_URL || '';

// ── helpers ──────────────────────────────────────────────────

const pad = (n: number) => String(n).padStart(2, '0');

function localIso(d: Date = new Date()) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const TODAY = localIso();

function fmtDate(iso: string): string {
  const [y, mo, day] = iso.split('-').map(Number);
  return new Date(y, mo - 1, day).toLocaleDateString('ro-RO', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

function fmtTime(t: string) {
  return t.substring(0, 5);
}

function durationLabel(start: string, end: string): string {
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const mins = (eh * 60 + em) - (sh * 60 + sm);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (m === 0) return `${h}h`;
  if (h === 0) return `${m}min`;
  return `${h}h${m}`;
}

function participantToUser(p: BookingParticipant): User {
  return {
    id: p.user_id,
    email: '',
    first_name: p.first_name,
    last_name: p.last_name,
    phone: null,
    avatar_url: p.avatar_url,
    status: 'active',
    email_verified: true,
    roles: [],
    level_name: p.level_name,
    created_at: '',
    updated_at: '',
  };
}

// ── status config ─────────────────────────────────────────────

type Status = Booking['status'];

const STATUS: Record<Status, { label: string; color: string; bg: string; border: string; accent: string }> = {
  confirmed: { label: 'Confirmată',    color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0', accent: '#16a34a' },
  pending:   { label: 'În așteptare', color: '#92400e', bg: '#fffbeb', border: '#fde68a', accent: '#f59e0b' },
  cancelled: { label: 'Anulată',       color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', accent: '#9ca3af' },
  completed: { label: 'Finalizată',    color: '#1e40af', bg: '#eff6ff', border: '#bfdbfe', accent: '#3b82f6' },
};

const FILTER_TABS = [
  { key: 'all',      label: 'Toate' },
  { key: 'upcoming', label: 'Viitoare' },
  { key: 'past',     label: 'Trecute' },
  { key: 'cancelled',label: 'Anulate' },
] as const;

type FilterKey = typeof FILTER_TABS[number]['key'];

// ── ParticipantAvatars ────────────────────────────────────────

const ParticipantAvatars: React.FC<{ participants: BookingParticipant[] }> = ({ participants }) => {
  const MAX = 4;
  const visible  = participants.slice(0, MAX);
  const overflow = participants.length - MAX;

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {visible.map((p, i) => {
        const src   = p.avatar_url ? `${SITE_URL}${p.avatar_url}` : null;
        const initl = `${p.first_name[0] ?? ''}${p.last_name[0] ?? ''}`.toUpperCase();
        return (
          <div
            key={p.user_id}
            title={`${p.first_name} ${p.last_name}${p.level_name ? ` · ${p.level_name}` : ''}`}
            style={{
              width: 26, height: 26, borderRadius: '50%',
              border: '2px solid var(--tt-surface)',
              marginLeft: i === 0 ? 0 : -8,
              background: src ? 'transparent' : 'var(--tt-primary)',
              overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 9, fontWeight: 700,
              position: 'relative', zIndex: visible.length - i,
              flexShrink: 0,
            }}
          >
            {src
              ? <img src={src} alt={p.first_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : initl
            }
          </div>
        );
      })}
      {overflow > 0 && (
        <div style={{
          width: 26, height: 26, borderRadius: '50%',
          border: '2px solid var(--tt-surface)',
          marginLeft: -8, background: 'var(--tt-surface-2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 9, fontWeight: 700, color: 'var(--tt-text-muted)',
          position: 'relative', zIndex: 0, flexShrink: 0,
        }}>
          +{overflow}
        </div>
      )}
    </div>
  );
};

// ── component ─────────────────────────────────────────────────

const Bookings: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [bookings, setBookings]       = useState<Booking[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [filter, setFilter]           = useState<FilterKey>('upcoming');

  // Cancel
  const [cancelTarget, setCancelTarget] = useState<Booking | null>(null);
  const [cancelling, setCancelling]   = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // Edit participants
  const [editTarget, setEditTarget]             = useState<Booking | null>(null);
  const [editParticipants, setEditParticipants] = useState<User[]>([]);
  const [editSaving, setEditSaving]             = useState(false);
  const [editError, setEditError]               = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await bookingsAPI.getAll({ my_bookings: true });
      if (res.success && res.data) {
        const sorted = [...(res.data.bookings || [])].sort((a, b) => {
          const aUp = a.booking_date >= TODAY && a.status !== 'cancelled';
          const bUp = b.booking_date >= TODAY && b.status !== 'cancelled';
          if (aUp !== bUp) return aUp ? -1 : 1;
          return a.booking_date < b.booking_date ? (aUp ? -1 : 1) : (aUp ? 1 : -1);
        });
        setBookings(sorted);
      } else {
        setError(res.message || 'Nu s-au putut încărca rezervările.');
      }
    } catch (err: any) {
      setError(err.message || 'A apărut o eroare.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    switch (filter) {
      case 'upcoming':  return bookings.filter(b => b.booking_date >= TODAY && b.status !== 'cancelled');
      case 'past':      return bookings.filter(b => b.booking_date < TODAY  && b.status !== 'cancelled');
      case 'cancelled': return bookings.filter(b => b.status === 'cancelled');
      default:          return bookings;
    }
  }, [bookings, filter]);

  const counts = useMemo(() => ({
    all:       bookings.length,
    upcoming:  bookings.filter(b => b.booking_date >= TODAY && b.status !== 'cancelled').length,
    past:      bookings.filter(b => b.booking_date < TODAY  && b.status !== 'cancelled').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length,
  }), [bookings]);

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setCancelError(null);
    setCancelling(true);
    try {
      const res = await bookingsAPI.update(cancelTarget.id, { status: 'cancelled' });
      if (res.success) {
        setCancelTarget(null);
        load();
      } else {
        setCancelError(res.message || 'Nu s-a putut anula rezervarea.');
      }
    } catch (err: any) {
      setCancelError(err.message || 'A apărut o eroare.');
    } finally {
      setCancelling(false);
    }
  };

  const openEditParticipants = (b: Booking) => {
    setEditTarget(b);
    setEditParticipants((b.participants ?? []).map(participantToUser));
    setEditError(null);
  };

  const handleSaveParticipants = async () => {
    if (!editTarget) return;
    setEditSaving(true);
    setEditError(null);
    try {
      const res = await bookingsAPI.updateParticipants(
        editTarget.id,
        editParticipants.map(p => p.id),
      );
      if (res.success) {
        setEditTarget(null);
        load();
      } else {
        setEditError(res.message || 'Eroare la salvare.');
      }
    } catch (err: any) {
      setEditError(err.message || 'A apărut o eroare.');
    } finally {
      setEditSaving(false);
    }
  };

  // ── render ────────────────────────────────────────────────

  return (
    <div className="page">
      <div className="container page-inner" style={{ maxWidth: 800 }}>

        {/* Header */}
        <div className="page-header d-flex justify-content-between align-items-start">
          <div>
            <h2>Rezervările mele</h2>
            <p>Istoricul și rezervările active la cluburi</p>
          </div>
          <Link to="/clubs" className="btn btn-primary" style={{ whiteSpace: 'nowrap' }}>
            + Rezervare nouă
          </Link>
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          {FILTER_TABS.map(tab => {
            const active = filter === tab.key;
            const count  = counts[tab.key];
            return (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '.4rem',
                  padding: '.4rem .9rem', borderRadius: 999,
                  border: `1.5px solid ${active ? 'var(--tt-primary)' : 'var(--tt-border)'}`,
                  background: active ? 'var(--tt-primary)' : 'var(--tt-surface)',
                  color: active ? '#fff' : 'var(--tt-text-2)',
                  fontWeight: active ? 600 : 400, fontSize: '.85rem',
                  cursor: 'pointer', transition: 'all .15s',
                }}
              >
                {tab.label}
                <span style={{
                  background: active ? 'rgba(255,255,255,.25)' : 'var(--tt-surface-2)',
                  color: active ? '#fff' : 'var(--tt-text-muted)',
                  borderRadius: 999, padding: '0 .45rem',
                  fontSize: '.72rem', fontWeight: 700, minWidth: 20, textAlign: 'center',
                }}>{count}</span>
              </button>
            );
          })}
        </div>

        {loading && (
          <div className="text-center py-5">
            <span className="spinner-border" style={{ color: 'var(--tt-primary)' }} />
            <p className="mt-3 text-muted">Se încarcă rezervările…</p>
          </div>
        )}

        {!loading && error && <div className="alert alert-danger">{error}</div>}

        {!loading && !error && filtered.length === 0 && (
          <div className="tt-empty">
            <div className="tt-empty-icon">📅</div>
            <h5>
              {filter === 'upcoming'  ? 'Nicio rezervare viitoare' :
               filter === 'past'      ? 'Niciun istoric' :
               filter === 'cancelled' ? 'Nicio rezervare anulată' :
               'Nicio rezervare încă'}
            </h5>
            <p>
              {filter === 'upcoming'
                ? 'Rezervă o masă la un club pentru a începe'
                : 'Încearcă o altă filtrare'}
            </p>
            {filter === 'upcoming' && (
              <Link to="/clubs" className="btn btn-primary mt-2">Găsește cluburi</Link>
            )}
          </div>
        )}

        {!loading && !error && filtered.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {filtered.map(b => (
              <BookingCard
                key={b.id}
                booking={b}
                onCancel={() => setCancelTarget(b)}
                onEditParticipants={() => openEditParticipants(b)}
              />
            ))}
          </div>
        )}

      </div>

      {/* ── Cancel modal ── */}
      <Modal
        isOpen={!!cancelTarget}
        onClose={() => { setCancelTarget(null); setCancelError(null); }}
        title="Anulează rezervarea"
        footer={
          <div style={{ display: 'flex', gap: '.75rem' }}>
            <button
              className="btn btn-outline-secondary"
              onClick={() => { setCancelTarget(null); setCancelError(null); }}
              disabled={cancelling}
              style={{ flex: 1, borderRadius: 10 }}
            >
              Înapoi
            </button>
            <button
              className="btn btn-danger"
              onClick={handleCancel}
              disabled={cancelling}
              style={{ flex: 2, borderRadius: 10, fontWeight: 700 }}
            >
              {cancelling
                ? <><span className="spinner-border spinner-border-sm me-2" />Se anulează…</>
                : 'Da, anulează rezervarea'}
            </button>
          </div>
        }
      >
        {cancelTarget && (
          <div>
            <p style={{ color: 'var(--tt-text-2)', marginBottom: '1rem' }}>
              Ești sigur că vrei să anulezi această rezervare? Acțiunea nu poate fi anulată.
            </p>
            <div style={{
              background: 'var(--tt-surface-2)', border: '1px solid var(--tt-border)',
              borderRadius: 12, padding: '1rem 1.25rem',
            }}>
              {([
                ['🏟️ Club',     cancelTarget.club_name || '—'],
                ['🏓 Masă',     `Masa ${cancelTarget.table_number}`],
                ['📅 Data',     fmtDate(cancelTarget.booking_date)],
                ['🕐 Interval', `${fmtTime(cancelTarget.start_time)} – ${fmtTime(cancelTarget.end_time)}`],
              ] as [string, string][]).map(([label, value]) => (
                <div key={label} style={{
                  display: 'flex', justifyContent: 'space-between',
                  padding: '.3rem 0', borderBottom: '1px solid var(--tt-border)', gap: '1rem',
                }}>
                  <span style={{ color: 'var(--tt-text-muted)', fontSize: '.85rem' }}>{label}</span>
                  <span style={{ fontWeight: 600, fontSize: '.85rem' }}>{value}</span>
                </div>
              ))}
            </div>
            {cancelError && (
              <div className="alert alert-danger mt-3 mb-0 py-2" style={{ fontSize: '.85rem' }}>
                {cancelError}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ── Edit participants modal ── */}
      <Modal
        isOpen={!!editTarget}
        onClose={() => { setEditTarget(null); setEditError(null); }}
        title="Jucători invitați"
        maxWidth={520}
        footer={
          <div style={{ display: 'flex', gap: '.75rem' }}>
            <button
              className="btn btn-outline-secondary"
              onClick={() => { setEditTarget(null); setEditError(null); }}
              disabled={editSaving}
              style={{ flex: 1, borderRadius: 10 }}
            >
              Anulează
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSaveParticipants}
              disabled={editSaving}
              style={{ flex: 2, borderRadius: 10, fontWeight: 700 }}
            >
              {editSaving
                ? <><span className="spinner-border spinner-border-sm me-2" />Se salvează…</>
                : 'Salvează lista'}
            </button>
          </div>
        }
      >
        {editTarget && (
          <div>
            {/* Booking context */}
            <div style={{
              background: 'var(--tt-surface-2)', border: '1px solid var(--tt-border)',
              borderRadius: 12, padding: '.75rem 1rem', marginBottom: '1.25rem',
              display: 'flex', gap: '.5rem', alignItems: 'center',
            }}>
              <span style={{ fontSize: '1.1rem' }}>🏓</span>
              <div style={{ fontSize: '.82rem', color: 'var(--tt-text-2)' }}>
                <strong>{editTarget.club_name}</strong>
                {' · '}Masa {editTarget.table_number}
                {' · '}
                {fmtDate(editTarget.booking_date)},
                {' '}{fmtTime(editTarget.start_time)} – {fmtTime(editTarget.end_time)}
              </div>
            </div>

            <p style={{ fontSize: '.8rem', color: 'var(--tt-text-muted)', marginBottom: '.875rem', lineHeight: 1.5 }}>
              Adaugă sau elimină jucătorii invitați. Noii invitați vor primi o notificare.
            </p>

            <PlayerSearchPicker
              selected={editParticipants}
              onAdd={u => setEditParticipants(prev => [...prev, u])}
              onRemove={id => setEditParticipants(prev => prev.filter(p => p.id !== id))}
              excludeIds={[
                ...(currentUser?.id ? [currentUser.id] : []),
                editTarget.user_id,
              ]}
            />

            {editError && (
              <div className="alert alert-danger mt-3 mb-0 py-2" style={{ fontSize: '.85rem' }}>
                {editError}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

// ── BookingCard ───────────────────────────────────────────────

const BookingCard: React.FC<{
  booking: Booking;
  onCancel: () => void;
  onEditParticipants: () => void;
}> = ({ booking: b, onCancel, onEditParticipants }) => {
  const st       = STATUS[b.status] ?? STATUS.completed;
  const isActive = b.status === 'confirmed' || b.status === 'pending';
  const isPast   = b.booking_date < TODAY;
  const dur      = durationLabel(b.start_time, b.end_time);
  const clubName = b.club_name || b.club?.name || 'Club';
  const hasParticipants = (b.participants?.length ?? 0) > 0;
  const canEdit  = isActive && !isPast;

  return (
    <div style={{
      background: 'var(--tt-surface)',
      border: '1px solid var(--tt-border)',
      borderRadius: 16,
      overflow: 'hidden',
      boxShadow: 'var(--sh-sm)',
      display: 'flex',
      opacity: b.status === 'cancelled' ? .72 : 1,
    }}>
      {/* Accent bar */}
      <div style={{ width: 5, background: st.accent, flexShrink: 0 }} />

      {/* Body */}
      <div style={{ flex: 1, padding: '1.1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '.6rem' }}>

        {/* Row 1: club + status */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
          <div>
            <Link
              to={clubPath({ id: b.club_id, name: clubName })}
              style={{
                fontWeight: 800, fontSize: '1rem', color: 'var(--tt-text)',
                textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '.35rem',
              }}
            >
              {clubName}
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                stroke="var(--tt-text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ flexShrink: 0 }}>
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
            </Link>
            <div style={{ fontSize: '.8rem', color: 'var(--tt-text-muted)', marginTop: 2 }}>
              🏓 Masa {b.table_number}{b.table_type ? ` · ${b.table_type}` : ''}
            </div>
          </div>

          <span style={{
            background: st.bg, color: st.color, border: `1px solid ${st.border}`,
            borderRadius: 999, padding: '.2rem .75rem', fontSize: '.75rem', fontWeight: 700,
            whiteSpace: 'nowrap', flexShrink: 0,
          }}>
            {st.label}
          </span>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: 'var(--tt-border)' }} />

        {/* Date + time */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.75rem', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
            <span style={{
              width: 32, height: 32, borderRadius: 9,
              background: isPast ? 'var(--tt-surface-2)' : '#eff6ff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.95rem', flexShrink: 0,
            }}>📅</span>
            <div style={{ fontWeight: 600, fontSize: '.85rem', color: 'var(--tt-text)' }}>
              {fmtDate(b.booking_date)}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
            <span style={{
              width: 32, height: 32, borderRadius: 9,
              background: isPast ? 'var(--tt-surface-2)' : '#eff6ff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.95rem', flexShrink: 0,
            }}>🕐</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: '.85rem', color: 'var(--tt-text)' }}>
                {fmtTime(b.start_time)} – {fmtTime(b.end_time)}
              </div>
              <div style={{ fontSize: '.72rem', color: 'var(--tt-text-muted)' }}>{dur}</div>
            </div>
          </div>
        </div>

        {/* Notes */}
        {b.notes && (
          <div style={{
            background: 'var(--tt-surface-2)', border: '1px solid var(--tt-border)',
            borderRadius: 8, padding: '.45rem .75rem',
            fontSize: '.8rem', color: 'var(--tt-text-2)',
            display: 'flex', gap: '.4rem',
          }}>
            <span style={{ flexShrink: 0 }}>💬</span>
            <span>{b.notes}</span>
          </div>
        )}

        {/* Participants row */}
        {(hasParticipants || canEdit) && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: '1rem', padding: '.5rem .75rem',
            background: 'var(--tt-surface-2)', borderRadius: 10,
            border: '1px solid var(--tt-border)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.55rem', minWidth: 0 }}>
              <span style={{ fontSize: '.78rem', color: 'var(--tt-text-muted)', fontWeight: 500, flexShrink: 0 }}>
                👥 Jucători:
              </span>
              {hasParticipants ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', minWidth: 0 }}>
                  <ParticipantAvatars participants={b.participants!} />
                  <span style={{ fontSize: '.75rem', color: 'var(--tt-text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {b.participants!.map(p => p.first_name).join(', ')}
                  </span>
                </div>
              ) : (
                <span style={{ fontSize: '.78rem', color: 'var(--tt-text-muted)', fontStyle: 'italic' }}>
                  Niciun jucător invitat
                </span>
              )}
            </div>

            {canEdit && (
              <button
                onClick={onEditParticipants}
                style={{
                  background: 'none', border: '1px solid var(--tt-border)',
                  borderRadius: 8, padding: '.2rem .65rem',
                  color: 'var(--tt-primary)', fontSize: '.75rem', fontWeight: 600,
                  cursor: 'pointer', flexShrink: 0, transition: 'background .15s',
                }}
              >
                Editează
              </button>
            )}
          </div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '.1rem' }}>
          <span style={{ fontSize: '.72rem', color: 'var(--tt-text-muted)' }}>
            Rezervat pe {new Date(b.created_at).toLocaleDateString('ro-RO')}
          </span>
          {canEdit && (
            <button
              onClick={onCancel}
              style={{
                background: 'none', border: '1px solid #fecaca',
                borderRadius: 8, padding: '.25rem .75rem',
                color: '#dc2626', fontSize: '.78rem', fontWeight: 600,
                cursor: 'pointer', transition: 'background .15s',
              }}
            >
              Anulează
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Bookings;
